import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconChevronRight } from '#src/atoms/icons/system/iconChevronRight.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { ReferrerCodeCountdownChip } from '#src/components/referral/ReferrerCodeCountdownChip/index.tsx';
import { Avatar } from '#src/components/user/Avatar/index.tsx';
import { ANY_SPORT_SERIES_PLAY } from '#src/constants/__generated__/routes.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useTimeLeft } from '#src/hooks/useTimeLeft.tsx';
import { isType } from '#src/lib/gql/index.ts';
import { generateSportPath } from '#src/lib/routing/generateSportPath.ts';

import type {
  InviteCodeStatusQuery,
  InviteCodeStatusQueryVariables,
} from './__generated__/index.graphql.ts';

const INVITE_CODE_STATUS_QUERY = gql`
  query InviteCodeStatusQuery($sport: Sport!) {
    cardSet {
      id
      currentSet(sport: $sport) {
        slug
        branding {
          id
          colors {
            surfacePrimary
          }
          marketPacksBackgroundDesktop: assetUrl(
            slot: MARKET_PACKS_BACKGROUND_DESKTOP
          )
        }
      }
    }
    currentUser {
      slug
      referrerCodeDeadline
      referralAsReferee(sport: $sport) {
        id
        referrer {
          ... on User {
            slug
            nickname
            ...Avatar_user
          }
          ... on UserSource {
            id
            name
            displayName
            pictureUrl
          }
        }
      }
    }
  }
  ${Avatar.fragments.user}
` as TypedDocumentNode<InviteCodeStatusQuery, InviteCodeStatusQueryVariables>;

const Root = styled(Horizontal)`
  gap: var(--unit);
  align-items: center;
  justify-content: space-between;
  padding: var(--double-unit);
  border: 1px solid var(--border-color, var(--c-nd-150));
  border-radius: var(--double-unit);
  width: 100%;
`;

const AffiliatePicture = styled.img`
  width: var(--double-and-a-half-unit);
  height: var(--double-and-a-half-unit);
  border-radius: 50%;
  object-fit: cover;
`;

type Props = {
  sport: Sport;
  onNavigate?: () => void;
};

export const InviteCodeStatus = ({ sport, onNavigate }: Props) => {
  const { data } = useQuery(INVITE_CODE_STATUS_QUERY, {
    variables: { sport },
  });
  const { currentUser } = data || {};
  const branding = data?.cardSet?.currentSet?.branding;
  const colors = branding?.colors;
  const backgroundUrl = branding?.marketPacksBackgroundDesktop;
  const referrer = currentUser?.referralAsReferee?.referrer;
  const referrerCodeDeadline = currentUser?.referrerCodeDeadline;
  const { isEnded } = useTimeLeft(referrerCodeDeadline);

  if (referrer) {
    const isUser = isType(referrer, 'User');
    const name = isUser ? referrer.nickname : referrer.displayName;
    if (!name) {
      return null;
    }
    return (
      <Root
        style={
          {
            ...(backgroundUrl
              ? {
                  background: `url(${backgroundUrl}) center / cover no-repeat`,
                }
              : {}),
            '--border-color':
              colors?.surfacePrimary ?? 'var(--c-nd-600-opaque)',
          } as CSSProperties
        }
      >
        <LabelM color="var(--c-white)">
          <FormattedMessage defaultMessage="You have been invited by" />
        </LabelM>
        <Horizontal center>
          {isUser ? (
            <Avatar user={referrer} size={2.5} rounded />
          ) : (
            !!referrer.pictureUrl && (
              <AffiliatePicture src={referrer.pictureUrl} alt="" />
            )
          )}
          <LabelM bold color="var(--c-white)">
            {name}
          </LabelM>
        </Horizontal>
      </Root>
    );
  }

  if (!referrerCodeDeadline || isEnded) {
    return null;
  }

  return (
    <Root
      as={Link}
      to={`${generateSportPath(ANY_SPORT_SERIES_PLAY, {
        sport,
      })}?dialog=invite-code`}
      onClick={onNavigate}
    >
      <LabelM bold>
        <FormattedMessage defaultMessage="Have an invite code?" />
      </LabelM>
      <Horizontal gap={1}>
        <ReferrerCodeCountdownChip deadline={referrerCodeDeadline} />
        <FontAwesomeIcon icon={iconChevronRight} color="var(--c-white)" />
      </Horizontal>
    </Root>
  );
};
